import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api/insolita';
import { Booking, BookingStatus, PaymentStatus, SlotAvailability, Tour } from '../../api/types';
import FlexibleAvailability from './FlexibleAvailability';

const STATUS_STYLE: Record<BookingStatus, string> = {
  CONFIRMED: 'bg-green-100 text-green-700',
  WAITLISTED: 'bg-amber-100 text-amber-700',
  PENDING: 'bg-neutral-100 text-neutral-500',
  CANCELLED: 'bg-red-100 text-red-600 line-through',
};
const STATUS_LABEL: Record<BookingStatus, string> = {
  CONFIRMED: 'Confermata', WAITLISTED: "Lista d'attesa", PENDING: 'In attesa email', CANCELLED: 'Annullata',
};
const PAY_OPTIONS: PaymentStatus[] = ['PENDING', 'PAID', 'FAILED', 'REFUNDED', 'NOT_REQUIRED'];

function fmt(d?: string) {
  return d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

const emptySlot = { tourId: 0, startDate: '', endDate: '', maxCapacity: 20, priceOverride: '', status: 'OPEN', notes: '' };

const SlotsAdmin: React.FC = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [slots, setSlots] = useState<SlotAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<any | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedTourId, setSelectedTourId] = useState<number | ''>('');

  const selectedTour = useMemo(() => tours.find((t) => t.id === selectedTourId), [tours, selectedTourId]);
  const isFlexible = selectedTour?.bookingMode === 'FLEXIBLE';
  const shownSlots = useMemo(
    () => (selectedTourId ? slots.filter((s) => s.tourId === selectedTourId) : slots),
    [slots, selectedTourId]
  );

  const load = () => {
    setLoading(true);
    Promise.all([adminApi.tours.list(), adminApi.slots.list()])
      .then(([t, s]) => { setTours(t); setSlots(s); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const loadBookings = (slotId: number) => {
    setExpanded(slotId);
    adminApi.bookings.bySlot(slotId).then(setBookings);
  };

  const saveSlot = async () => {
    const body = {
      tourId: Number(draft.tourId),
      startDate: draft.startDate,
      endDate: draft.endDate || null,
      maxCapacity: Number(draft.maxCapacity),
      priceOverride: draft.priceOverride === '' ? null : Number(draft.priceOverride),
      status: draft.status,
      notes: draft.notes,
    };
    if (draft.id) await adminApi.slots.update(draft.id, body);
    else await adminApi.slots.create(body);
    setDraft(null);
    load();
  };

  const removeSlot = async (id: number) => {
    if (!window.confirm('Eliminare questo slot e le sue prenotazioni?')) return;
    await adminApi.slots.remove(id);
    if (expanded === id) setExpanded(null);
    load();
  };

  const act = async (fn: Promise<any>, slotId: number) => {
    await fn;
    loadBookings(slotId);
    load();
  };

  const input = 'w-full border border-neutral-200 rounded px-3 py-2 text-sm';

  if (loading) return <p className="text-neutral-500">Caricamento…</p>;

  return (
    <div>
      <div className="flex flex-wrap justify-between items-end gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Disponibilità & Prenotazioni</h1>
          <p className="text-neutral-500 text-sm">
            {isFlexible
              ? 'Tour a calendario: imposta la disponibilità giorno per giorno; il cliente sceglie liberamente il periodo.'
              : 'Partenze fisse: crea date con capienza massima. Oltre la capienza i clienti entrano in lista d\'attesa e vengono promossi automaticamente quando qualcuno annulla.'}
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Tour</label>
            <select value={selectedTourId} onChange={(e) => setSelectedTourId(e.target.value ? Number(e.target.value) : '')}
              className="border border-neutral-200 rounded px-3 py-2 text-sm bg-white">
              <option value="">Tutti (a partenze fisse)</option>
              {tours.map((t) => (
                <option key={t.id} value={t.id}>{t.title}{t.bookingMode === 'FLEXIBLE' ? ' · calendario' : ''}</option>
              ))}
            </select>
          </div>
          {!isFlexible && (
            <button onClick={() => setDraft({ ...emptySlot, tourId: selectedTourId || tours[0]?.id || 0 })} disabled={!tours.length}
              className="px-4 py-2 bg-[#E4FF1A] text-[#0B0B0C] font-medium rounded text-sm disabled:opacity-50">+ Nuovo slot</button>
          )}
        </div>
      </div>

      {isFlexible && selectedTour ? (
        <FlexibleAvailability tour={selectedTour} />
      ) : (
      <div className="space-y-3">
        {shownSlots.map((s) => (
          <div key={s.id} className="bg-white border border-neutral-200 rounded-lg">
            <div className="p-4 flex flex-wrap justify-between items-center gap-3">
              <div>
                <p className="font-medium">{s.tourTitle}</p>
                <p className="text-sm text-neutral-500">{fmt(s.startDate)}{s.endDate ? ` → ${fmt(s.endDate)}` : ''} · €{s.pricePerPersonPerDay}/p/g · {s.status === 'CLOSED' ? 'Chiuso' : 'Aperto'}</p>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-green-700 font-medium">{s.confirmedSeats}/{s.maxCapacity} confermati</span>
                <span className="text-neutral-400">·</span>
                <span className="text-green-600">{s.availableSeats} liberi</span>
                {s.waitlistSeats > 0 && <span className="text-amber-600 font-medium">{s.waitlistSeats} in lista d'attesa</span>}
              </div>
              <div className="flex gap-3">
                <button onClick={() => (expanded === s.id ? setExpanded(null) : loadBookings(s.id))} className="text-sm text-[#0B0B0C] font-medium hover:underline">
                  {expanded === s.id ? 'Chiudi' : 'Prenotazioni'}
                </button>
                <button onClick={() => setDraft({ id: s.id, tourId: s.tourId, startDate: s.startDate, endDate: s.endDate || '', maxCapacity: s.maxCapacity, priceOverride: '', status: s.status, notes: '' })} className="text-sm text-blue-600 hover:underline">Modifica</button>
                <button onClick={() => removeSlot(s.id)} className="text-sm text-red-500 hover:underline">Elimina</button>
              </div>
            </div>

            {expanded === s.id && (
              <div className="border-t border-neutral-100 p-4 bg-neutral-50">
                {bookings.length === 0 ? (
                  <p className="text-sm text-neutral-400">Nessuna prenotazione.</p>
                ) : (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead>
                      <tr className="text-left text-neutral-400 text-xs uppercase">
                        <th className="py-2">Cliente</th><th>Persone</th><th>Stato</th><th>Pagamento</th><th>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((b) => (
                        <tr key={b.id} className="border-t border-neutral-200">
                          <td className="py-2">
                            <div className="font-medium">{b.customerName}</div>
                            <div className="text-xs text-neutral-400">{b.customerEmail} · {b.customerPhone}</div>
                          </td>
                          <td>{b.people}</td>
                          <td><span className={`text-xs px-2 py-1 rounded ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span></td>
                          <td>
                            <select value={b.paymentStatus} onChange={(e) => act(adminApi.bookings.setPayment(b.id, e.target.value as PaymentStatus), s.id)}
                              className="text-xs border border-neutral-200 rounded px-1 py-1 bg-white">
                              {PAY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </td>
                          <td className="space-x-2">
                            {b.status !== 'CONFIRMED' && b.status !== 'CANCELLED' && (
                              <button onClick={() => act(adminApi.bookings.confirm(b.id), s.id)} className="text-xs text-green-600 hover:underline">Conferma</button>
                            )}
                            {b.status !== 'CANCELLED' && (
                              <button onClick={() => act(adminApi.bookings.cancel(b.id), s.id)} className="text-xs text-red-500 hover:underline">Annulla</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {shownSlots.length === 0 && <p className="text-neutral-400 text-sm">Nessuno slot. Creane uno per aprire le prenotazioni.</p>}
      </div>
      )}

      {draft && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setDraft(null)}>
          <div className="bg-white rounded-lg w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-4">{draft.id ? 'Modifica slot' : 'Nuovo slot'}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Tour</label>
                <select className={input} value={draft.tourId} onChange={(e) => setDraft({ ...draft, tourId: Number(e.target.value) })}>
                  {tours.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
              <div><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Data inizio</label><input type="date" className={input} value={draft.startDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} /></div>
              <div><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Data fine</label><input type="date" className={input} value={draft.endDate} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} /></div>
              <div><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Capienza max</label><input type="number" className={input} value={draft.maxCapacity} onChange={(e) => setDraft({ ...draft, maxCapacity: e.target.value })} /></div>
              <div><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Prezzo p/g override (€)</label><input type="number" className={input} value={draft.priceOverride} placeholder="default tour" onChange={(e) => setDraft({ ...draft, priceOverride: e.target.value })} /></div>
              <div><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Stato</label>
                <select className={input} value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
                  <option value="OPEN">Aperto</option><option value="CLOSED">Chiuso</option>
                </select>
              </div>
              <div className="col-span-2"><label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Note</label><input className={input} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setDraft(null)} className="px-4 py-2 text-sm text-neutral-500">Annulla</button>
              <button onClick={saveSlot} className="px-4 py-2 bg-[#0B0B0C] text-white rounded text-sm">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlotsAdmin;
